import * as __typia_transform__isTypeUint32 from "typia/lib/internal/_isTypeUint32";
import typia, { type tags } from 'typia';
import isEqual from 'lodash/isEqual';
import { dispatchAction } from '@ardoq/rxbeach';
import type { RenderClaimType, RenderEntityType } from '@ardoq/api-types';
import { transformationsApi } from '@ardoq/api';
import { isArdoqError, type Maybe } from '@ardoq/common-helpers';
import { currentDateTimeISO } from '@ardoq/date-time';
import { futureStateDraftsChanged } from 'transformations/futureStatePage/futureStateDraftClaims$';
import { mergeDrafts, toRenderRequest, type DraftAlias, type FutureStateDrafts, } from 'transformations/futureStatePage/futureStateDrafts';
import { AiControlledCommandId } from 'aiControlledCommands/aiControlledCommandId';
import { defineAiControlledCommand, type CommandValidationResult, } from 'aiControlledCommands/defineAiControlledCommand';
import type { AiControlledCommand, AiControlledCommandDefinition, CommandRejection, } from 'aiControlledCommands/types';
import { FUTURE_STATE_DOMAIN } from 'aiControlledCommands/futureState/domain';
import { createDraftId, type DraftId } from '../draftClaimId';
import type { FutureStateDraftCommandContext } from './context';
import { DRAFT_CHANGES_CONTEXT_DESCRIPTION, WHEN_TO_USE } from './descriptions';
import { applyDraftChanges, type FutureStateDraftExecutionState, } from './applyDraftChanges';
import { draftBatchRejected, isCurrentRejection, type RejectedBatch, } from './rejectedBatch$';
// Open objects: typia emits no additionalProperties, so custom fields pass.
type NewComponentProperties = {
    rootWorkspace: DraftId;
    typeId: string & tags.Pattern<'^p[0-9]+$'>;
    name: string & tags.MinLength<1> & tags.MaxLength<500>;
    parent: Maybe<DraftAlias>;
};
type NewReferenceProperties = {
    rootWorkspace: DraftId;
    type: number & tags.Type<'uint32'>;
    source: DraftAlias;
    target: DraftAlias;
};
type ComponentCreation = {
    kind: 'createComponent';
    alias?: string;
    claimGroupId?: DraftAlias;
    assertProperties: NewComponentProperties;
};
type ReferenceCreation = {
    kind: 'createReference';
    alias?: string;
    claimGroupId?: DraftAlias;
    assertProperties: NewReferenceProperties;
};
type TargetedClaimCreation = {
    kind: 'create';
    alias?: string;
    claimType: RenderClaimType;
    entityType: RenderEntityType;
    targetId: DraftAlias;
    claimGroupId?: DraftAlias;
    assertProperties?: Record<string, unknown>;
};
export type ClaimCreation = ComponentCreation | ReferenceCreation | TargetedClaimCreation;
export type ClaimUpdate = {
    kind: 'update';
    claimId: DraftAlias;
    claimGroupId?: DraftAlias | null;
    assertProperties?: Record<string, unknown>;
};
export type DraftClaimChange = ClaimCreation | ClaimUpdate | {
    kind: 'delete';
    claimId: DraftAlias ;
} | {
    kind: 'discard';
    claimId: DraftAlias;
};
export type DraftClaimGroupChange = {
    kind: 'create';
    alias: string;
    name: string;
    description?: string;
    } | {
    kind: 'update';
    claimGroupId: DraftAlias;
    name: string;
    description?: string | null;
    } | {
    kind: 'delete';
    claimGroupId: DraftAlias ;
} | {
    kind: 'discard';
    claimGroupId: DraftAlias;
};
export type DraftFutureStateChangesPayload = {
    claimGroups: DraftClaimGroupChange[];
    claims: DraftClaimChange[];
};
const DRAFT_CHANGES_PARAMETERS = {
    version: "3.1",
    components: {
        schemas: {
            DraftFutureStateChangesPayload: {
                type: "object",
                properties: {
                    claimGroups: {
                        type: "array",
                        items: {
                            $ref: "#/components/schemas/DraftClaimGroupChange"
                        }
                    },
                    claims: {
                        type: "array",
                        items: {
                            $ref: "#/components/schemas/DraftClaimChange"
                        }
                    }
                },
                required: [
                    "claimGroups",
                    "claims"
                ]
            },
            DraftClaimGroupChange: {
                oneOf: [
                    {
                        type: "object",
                        properties: {
                            kind: {
                                "const": "create"
                            },
                            alias: {
                                type: "string"
                            },
                            name: {
                                type: "string"
                            },
                            description: {
                                type: "string"
                            }
                        },
                        required: [
                            "kind",
                            "alias",
                            "name"
                        ]
                    },
                    {
                        type: "object",
                        properties: {
                            kind: {
                                "const": "update"
                            },
                            claimGroupId: {
                                type: "string"
                            },
                            name: {
                                type: "string"
                            },
                            description: {
                                oneOf: [
                                    {
                                        type: "null"
                                    },
                                    {
                                        type: "string"
                                    }
                                ]
                            }
                        },
                        required: [
                            "kind",
                            "claimGroupId",
                            "name"
                        ]
                    },
                    {
                        type: "object",
                        properties: {
                            kind: {
                                "const": "delete"
                            },
                            claimGroupId: {
                                type: "string"
                            }
                        },
                        required: [
                            "kind",
                            "claimGroupId"
                        ]
                    },
                    {
                        type: "object",
                        properties: {
                            kind: {
                                "const": "discard"
                            },
                            claimGroupId: {
                                type: "string"
                            }
                        },
                        required: [
                            "kind",
                            "claimGroupId"
                        ]
                    }
                ]
            },
            DraftClaimChange: {
                oneOf: [
                    {
                        type: "object",
                        properties: {
                            kind: {
                                "const": "delete"
                            },
                            claimId: {
                                type: "string"
                            }
                        },
                        required: [
                            "kind",
                            "claimId"
                        ]
                    },
                    {
                        type: "object",
                        properties: {
                            kind: {
                                "const": "discard"
                            },
                            claimId: {
                                type: "string"
                            }
                        },
                        required: [
                            "kind",
                            "claimId"
                        ]
                    },
                    {
                        $ref: "#/components/schemas/ComponentCreation"
                    },
                    {
                        $ref: "#/components/schemas/ReferenceCreation"
                    },
                    {
                        $ref: "#/components/schemas/TargetedClaimCreation"
                    },
                    {
                        $ref: "#/components/schemas/ClaimUpdate"
                    }
                ]
            },
            ComponentCreation: {
                type: "object",
                properties: {
                    kind: {
                        "const": "createComponent"
                    },
                    alias: {
                        type: "string"
                    },
                    claimGroupId: {
                        type: "string"
                    },
                    assertProperties: {
                        $ref: "#/components/schemas/NewComponentProperties"
                    }
                },
                required: [
                    "kind",
                    "assertProperties"
                ]
            },
            NewComponentProperties: {
                type: "object",
                properties: {
                    rootWorkspace: {
                        $ref: "#/components/schemas/DraftId"
                    },
                    typeId: {
                        type: "string",
                        pattern: "^p[0-9]+$"
                    },
                    name: {
                        type: "string",
                        minLength: 1,
                        maxLength: 500
                    },
                    parent: {
                        $ref: "#/components/schemas/Maybestring"
                    }
                },
                required: [
                    "rootWorkspace",
                    "typeId",
                    "name",
                    "parent"
                ]
            },
            DraftId: {
                type: "string",
                pattern: "^[0-9a-f]{24}$"
            },
            Maybestring: {
                oneOf: [
                    {
                        type: "null"
                    },
                    {
                        type: "string"
                    }
                ]
            },
            ReferenceCreation: {
                type: "object",
                properties: {
                    kind: {
                        "const": "createReference"
                    },
                    alias: {
                        type: "string"
                    },
                    claimGroupId: {
                        type: "string"
                    },
                    assertProperties: {
                        $ref: "#/components/schemas/NewReferenceProperties"
                    }
                },
                required: [
                    "kind",
                    "assertProperties"
                ]
            },
            NewReferenceProperties: {
                type: "object",
                properties: {
                    rootWorkspace: {
                        $ref: "#/components/schemas/DraftId"
                    },
                    type: {
                        type: "integer",
                        minimum: 0
                    },
                    source: {
                        type: "string"
                    },
                    target: {
                        type: "string"
                    }
                },
                required: [
                    "rootWorkspace",
                    "type",
                    "source",
                    "target"
                ]
            },
            TargetedClaimCreation: {
                type: "object",
                properties: {
                    kind: {
                        "const": "create"
                    },
                    alias: {
                        type: "string"
                    },
                    claimType: {
                        $ref: "#/components/schemas/RenderClaimType"
                    },
                    entityType: {
                        $ref: "#/components/schemas/RenderEntityType"
                    },
                    targetId: {
                        type: "string"
                    },
                    claimGroupId: {
                        type: "string"
                    },
                    assertProperties: {
                        $ref: "#/components/schemas/Recordstringunknown"
                    }
                },
                required: [
                    "kind",
                    "claimType",
                    "entityType",
                    "targetId"
                ]
            },
            RenderClaimType: {
                oneOf: [
                    {
                        "const": "presence"
                    },
                    {
                        "const": "absence"
                    }
                ]
            },
            RenderEntityType: {
                oneOf: [
                    {
                        "const": "component"
                    },
                    {
                        "const": "reference"
                    }
                ]
            },
            Recordstringunknown: {
                type: "object",
                properties: {},
                required: [],
                description: "Construct a type with a set of properties K of type T",
                additionalProperties: {}
            },
            ClaimUpdate: {
                type: "object",
                properties: {
                    kind: {
                        "const": "update"
                    },
                    claimId: {
                        type: "string"
                    },
                    claimGroupId: {
                        oneOf: [
                            {
                                type: "null"
                            },
                            {
                                type: "string"
                            }
                        ]
                    },
                    assertProperties: {
                        $ref: "#/components/schemas/Recordstringunknown"
                    }
                },
                required: [
                    "kind",
                    "claimId"
                ]
            }
        }
    },
    schema: {
        $ref: "#/components/schemas/DraftFutureStateChangesPayload"
    }
} as import("typia").IJsonSchemaUnit<"3.1">;
const isDraftChangesPayload = (() => { const _io0 = (input: any): boolean => Array.isArray(input.claimGroups) && input.claimGroups.every((elem: any) => "object" === typeof elem && null !== elem && _iu0(elem)) && (Array.isArray(input.claims) && input.claims.every((elem: any) => "object" === typeof elem && null !== elem && _iu1(elem))); const _io1 = (input: any): boolean => "create" === input.kind && "string" === typeof input.alias && "string" === typeof input.name && (undefined === input.description || "string" === typeof input.description); const _io2 = (input: any): boolean => "update" === input.kind && "string" === typeof input.claimGroupId && "string" === typeof input.name && (null === input.description || undefined === input.description || "string" === typeof input.description); const _io3 = (input: any): boolean => "delete" === input.kind && "string" === typeof input.claimGroupId; const _io4 = (input: any): boolean => "discard" === input.kind && "string" === typeof input.claimGroupId; const _io5 = (input: any): boolean => "createComponent" === input.kind && (undefined === input.alias || "string" === typeof input.alias) && (undefined === input.claimGroupId || "string" === typeof input.claimGroupId) && ("object" === typeof input.assertProperties && null !== input.assertProperties && _io6(input.assertProperties)); const _io6 = (input: any): boolean => "string" === typeof input.rootWorkspace && RegExp("^[0-9a-f]{24}$").test(input.rootWorkspace) && ("string" === typeof input.typeId && RegExp("^p[0-9]+$").test(input.typeId)) && ("string" === typeof input.name && (1 <= input.name.length && input.name.length <= 500)) && (null === input.parent || "string" === typeof input.parent); const _io7 = (input: any): boolean => "createReference" === input.kind && (undefined === input.alias || "string" === typeof input.alias) && (undefined === input.claimGroupId || "string" === typeof input.claimGroupId) && ("object" === typeof input.assertProperties && null !== input.assertProperties && _io8(input.assertProperties)); const _io8 = (input: any): boolean => "string" === typeof input.rootWorkspace && RegExp("^[0-9a-f]{24}$").test(input.rootWorkspace) && ("number" === typeof input.type && __typia_transform__isTypeUint32._isTypeUint32(input.type)) && "string" === typeof input.source && "string" === typeof input.target; const _io9 = (input: any): boolean => "create" === input.kind && (undefined === input.alias || "string" === typeof input.alias) && ("presence" === input.claimType || "absence" === input.claimType) && ("component" === input.entityType || "reference" === input.entityType) && "string" === typeof input.targetId && (undefined === input.claimGroupId || "string" === typeof input.claimGroupId) && (undefined === input.assertProperties || "object" === typeof input.assertProperties && null !== input.assertProperties && false === Array.isArray(input.assertProperties) && _io10(input.assertProperties)); const _io10 = (input: any): boolean => Object.keys(input).every((key: any) => {
    const value = input[key];
    if (undefined === value)
        return true;
    return true;
}); const _io11 = (input: any): boolean => "update" === input.kind && "string" === typeof input.claimId && (null === input.claimGroupId || undefined === input.claimGroupId || "string" === typeof input.claimGroupId) && (undefined === input.assertProperties || "object" === typeof input.assertProperties && null !== input.assertProperties && false === Array.isArray(input.assertProperties) && _io10(input.assertProperties)); const _io12 = (input: any): boolean => "delete" === input.kind && "string" === typeof input.claimId; const _io13 = (input: any): boolean => "discard" === input.kind && "string" === typeof input.claimId; const _iu0 = (input: any): any => (() => {
    if ("create" === input.kind)
        return _io1(input);
    else if ("update" === input.kind)
        return _io2(input);
    else if ("discard" === input.kind)
        return _io4(input);
    else if ("delete" === input.kind)
        return _io3(input);
    else
        return false;
})(); const _iu1 = (input: any): any => (() => {
    if ("createReference" === input.kind)
        return _io7(input);
    else if ("createComponent" === input.kind)
        return _io5(input);
    else if ("create" === input.kind)
        return _io9(input);
    else if ("update" === input.kind)
        return _io11(input);
    else if ("discard" === input.kind)
        return _io13(input);
    else if ("delete" === input.kind)
        return _io12(input);
    else
        return false;
})(); return (input: any): input is DraftFutureStateChangesPayload => "object" === typeof input && null !== input && _io0(input); })();
const isFutureStateDraftCommandContext = (() => { const _io0 = (input: any): boolean => "string" === typeof input.futureStateId && ("object" === typeof input.render && null !== input.render && _iu0(input.render)) && (null === input.rejectedBatch || "object" === typeof input.rejectedBatch && null !== input.rejectedBatch && _io4(input.rejectedBatch)) && (Array.isArray(input.claimGroups) && input.claimGroups.every((elem: any) => "object" === typeof elem && null !== elem && _io5(elem))) && (Array.isArray(input.claims) && input.claims.every((elem: any) => "object" === typeof elem && null !== elem && _io6(elem))) && (null === input.completion || "object" === typeof input.completion && null !== input.completion && _io9(input.completion)) && (null === input.claimExecutionStatus || "ok" === input.claimExecutionStatus || "blocked" === input.claimExecutionStatus || "impossible" === input.claimExecutionStatus); const _io1 = (input: any): boolean => "loading" === input.status; const _io2 = (input: any): boolean => "error" === input.status; const _io3 = (input: any): boolean => "ready" === input.status && "number" === typeof input.renderedDraftVersion; const _io4 = (input: any): boolean => "string" === typeof input.reason; const _io5 = (input: any): boolean => "string" === typeof input.alias && (null === input.draft || "create" === input.draft || "update" === input.draft || "delete" === input.draft) && "string" === typeof input.name && (null === input.description || "string" === typeof input.description); const _io6 = (input: any): boolean => "string" === typeof input.alias && (null === input.draft || "create" === input.draft || "update" === input.draft || "delete" === input.draft) && ("presence" === input.claimType || "absence" === input.claimType) && ("component" === input.entityType || "reference" === input.entityType) && (null === input.targetId || "string" === typeof input.targetId) && (null === input.claimGroupAlias || "string" === typeof input.claimGroupAlias) && (null === input.assertProperties || "object" === typeof input.assertProperties && null !== input.assertProperties && false === Array.isArray(input.assertProperties) && _io7(input.assertProperties)) && (null === input.evaluation || "object" === typeof input.evaluation && null !== input.evaluation && _io8(input.evaluation)); const _io7 = (input: any): boolean => Object.keys(input).every((key: any) => {
    const value = input[key];
    if (undefined === value)
        return true;
    return true;
}); const _io8 = (input: any): boolean => ("satisfied" === input.status || "unmet" === input.status) && ("create" === input.op || "delete" === input.op || "none" === input.op || "patch" === input.op) && (null === input.reason || "missing-target" === input.reason || "broken-structure" === input.reason || "duplicate-claim" === input.reason || "ambiguous-identity" === input.reason || "ambiguous-structure" === input.reason || "invalid-fields" === input.reason) && (Array.isArray(input.conflictingClaimAliases) && input.conflictingClaimAliases.every((elem: any) => "string" === typeof elem)); const _io9 = (input: any): boolean => "number" === typeof input.total && "number" === typeof input.satisfied; const _iu0 = (input: any): any => (() => {
    if ("error" === input.status)
        return _io2(input);
    else if ("loading" === input.status)
        return _io1(input);
    else if ("ready" === input.status)
        return _io3(input);
    else
        return false;
})(); return (input: any): input is FutureStateDraftCommandContext => "object" === typeof input && null !== input && _io0(input); })();
const RENDER_FAILED = 'The future state could not be rendered with these changes.';
const INVALID_PAYLOAD: CommandRejection = {
    status: 'rejected',
    reason: 'The AI returned invalid future state changes.',
};
const alreadyRejected = (reason: string): CommandRejection => ({
    status: 'rejected',
    reason: `This batch was already rejected (${reason}) and nothing was drafted. ` +
        'Do not resend it. Tell the user that the change could not be made.',
});
type ValidatedBatch = {
    changes: DraftFutureStateChangesPayload;
    next: FutureStateDrafts;
};
const validateBatch = (rawPayload: unknown, executionState: FutureStateDraftExecutionState, rejected: RejectedBatch | null): CommandValidationResult<ValidatedBatch> => {
    if (!isDraftChangesPayload(rawPayload))
        return INVALID_PAYLOAD;
    if (isCurrentRejection(rejected, executionState.drafts) &&
        isEqual(rejected.changes, rawPayload)) {
        return alreadyRejected(rejected.reason);
    }
    const applied = applyDraftChanges(rawPayload, executionState, {
        newId: createDraftId,
        now: currentDateTimeISO(),
    });
    return applied.status === 'rejected'
        ? applied
        : {
            status: 'valid',
            payload: { changes: rawPayload, next: applied.payload },
        };
};
const draftIfRenderable = async ({ changes, next }: ValidatedBatch, { key, drafts, persisted }: FutureStateDraftExecutionState): Promise<void> => {
    if (!persisted)
        throw new Error('Future state claims are not loaded');
    const body = toRenderRequest(mergeDrafts({ persisted, drafts: next }));
    const isEmpty = persisted.claims.length === 0 && body.claims.length === 0;
    const result = isEmpty
        ? null
        : await transformationsApi.renderFutureState({ ...key, body });
    if (isArdoqError(result)) {
        const reason = result.userMessage ?? RENDER_FAILED;
        dispatchAction(draftBatchRejected({
            futureStateId: next.futureStateId,
            basedOnVersion: drafts.latestDraftVersion,
            reason,
            changes,
        }));
        throw new Error(`Draft batch rejected by render: ${reason}`);
    }
    dispatchAction(futureStateDraftsChanged({
        next,
        basedOnVersion: drafts.latestDraftVersion,
    }));
};
export const buildDraftChangesCommand = (context: FutureStateDraftCommandContext, executionState: FutureStateDraftExecutionState, rejected: RejectedBatch | null): AiControlledCommand<AiControlledCommandDefinition<FutureStateDraftCommandContext>> => ({
    ...defineAiControlledCommand<ValidatedBatch, FutureStateDraftCommandContext>({
        name: AiControlledCommandId.DRAFT_FUTURE_STATE_CHANGES,
        requiresAppShell: true,
        domain: FUTURE_STATE_DOMAIN,
        description: WHEN_TO_USE,
        parameters: DRAFT_CHANGES_PARAMETERS,
        contextDescription: DRAFT_CHANGES_CONTEXT_DESCRIPTION,
        isContext: isFutureStateDraftCommandContext,
        validate: rawPayload => validateBatch(rawPayload, executionState, rejected),
        execute: batch => draftIfRenderable(batch, executionState),
        isApplied: ({ next }, { futureStateId, render }) => futureStateId === next.futureStateId &&
            render.status === 'ready' &&
            render.renderedDraftVersion >= next.latestDraftVersion,
    }),
    context,
});
